<template>
  <div class="dashboard-view">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <el-icon><User /></el-icon>
          个人中心
        </h1>
        <p class="page-subtitle">管理您的漫剧项目</p>
      </div>
      <el-button type="primary" size="large" @click="showCreateDialog = true">
        <el-icon><Plus /></el-icon>
        创建新项目
      </el-button>
    </div>

    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stats-row">
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-icon" style="background: #ecf5ff; color: #409eff;">
            <el-icon><Folder /></el-icon>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ stats.total }}</div>
            <div class="stat-label">总项目</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-icon" style="background: #f0f9eb; color: #67c23a;">
            <el-icon><VideoPlay /></el-icon>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ stats.completed }}</div>
            <div class="stat-label">已完成</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-icon" style="background: #fdf6ec; color: #e6a23c;">
            <el-icon><Loading /></el-icon>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ stats.inProgress }}</div>
            <div class="stat-label">进行中</div>
          </div>
        </el-card>
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-icon" style="background: #f4f4f5; color: #909399;">
            <el-icon><Document /></el-icon>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ stats.draft }}</div>
            <div class="stat-label">草稿</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 项目列表 -->
    <el-card class="projects-card" shadow="never">
      <template #header>
        <div class="card-header">
          <span class="card-title">我的项目</span>
          <div class="header-actions">
            <el-input
              v-model="searchQuery"
              placeholder="搜索项目..."
              clearable
              style="width: 200px"
              @input="handleSearch"
            >
              <template #prefix>
                <el-icon><Search /></el-icon>
              </template>
            </el-input>
            <el-select v-model="statusFilter" placeholder="状态筛选" clearable style="width: 120px">
              <el-option label="全部" value="" />
              <el-option label="草稿" value="draft" />
              <el-option label="进行中" value="in_progress" />
              <el-option label="已完成" value="completed" />
            </el-select>
          </div>
        </div>
      </template>

      <!-- 加载状态 -->
      <el-skeleton v-if="loading" :rows="5" animated />

      <!-- 空状态 -->
      <el-empty v-else-if="filteredProjects.length === 0" description="暂无项目">
        <el-button type="primary" @click="showCreateDialog = true">创建第一个项目</el-button>
      </el-empty>

      <!-- 项目表格 -->
      <el-table v-else :data="filteredProjects" style="width: 100%" v-loading="loading">
        <el-table-column label="项目名称" min-width="200">
          <template #default="{ row }">
            <div class="project-name-cell">
              <el-icon class="project-icon"><VideoCamera /></el-icon>
              <div class="project-info">
                <div class="project-name">{{ row.project_name }}</div>
                <div class="project-desc" v-if="row.description">{{ row.description }}</div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="120">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small">
              {{ getStatusLabel(row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="进度" width="200">
          <template #default="{ row }">
            <div class="progress-cell">
              <el-progress 
                :percentage="getProgressPercent(row.status)" 
                :status="getProgressStatus(row.status)"
                :stroke-width="8"
              />
              <span class="progress-text">{{ getProgressLabel(row.status) }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="创建时间" width="180">
          <template #default="{ row }">
            <span class="time-text">{{ formatDate(row.created_at) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <el-button-group>
              <el-button type="primary" size="small" @click="enterProject(row)">
                <el-icon><Edit /></el-icon>
                进入
              </el-button>
              <el-button size="small" @click="editProject(row)">
                <el-icon><Setting /></el-icon>
              </el-button>
              <el-button type="danger" size="small" @click="confirmDelete(row)">
                <el-icon><Delete /></el-icon>
              </el-button>
            </el-button-group>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-wrapper" v-if="filteredProjects.length > 0">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 创建项目对话框 -->
    <el-dialog
      v-model="showCreateDialog"
      title="创建新项目"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form ref="createFormRef" :model="createForm" :rules="createRules" label-width="80px">
        <el-form-item label="项目名称" prop="title">
          <el-input v-model="createForm.title" placeholder="请输入项目名称" maxlength="100" show-word-limit />
        </el-form-item>
        <el-form-item label="项目描述" prop="description">
          <el-input
            v-model="createForm.description"
            type="textarea"
            :rows="4"
            placeholder="请输入项目描述（可选）"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showCreateDialog = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="handleCreate">创建</el-button>
      </template>
    </el-dialog>

    <!-- 编辑项目对话框 -->
    <el-dialog
      v-model="showEditDialog"
      title="编辑项目"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form ref="editFormRef" :model="editForm" :rules="createRules" label-width="80px">
        <el-form-item label="项目名称" prop="title">
          <el-input v-model="editForm.title" placeholder="请输入项目名称" maxlength="100" show-word-limit />
        </el-form-item>
        <el-form-item label="项目描述" prop="description">
          <el-input
            v-model="editForm.description"
            type="textarea"
            :rows="4"
            placeholder="请输入项目描述（可选）"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showEditDialog = false">取消</el-button>
        <el-button type="primary" :loading="updating" @click="handleUpdate">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getProjects, createProject, updateProject, deleteProject } from '../api/project'

const router = useRouter()

// 状态
const loading = ref(false)
const projects = ref([])
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)
const searchQuery = ref('')
const statusFilter = ref('')

// 创建项目
const showCreateDialog = ref(false)
const creating = ref(false)
const createFormRef = ref(null)
const createForm = reactive({
  title: '',
  description: ''
})
const createRules = {
  title: [
    { required: true, message: '请输入项目名称', trigger: 'blur' },
    { min: 2, max: 100, message: '长度在 2 到 100 个字符', trigger: 'blur' }
  ]
}

// 编辑项目
const showEditDialog = ref(false)
const updating = ref(false)
const editFormRef = ref(null)
const editForm = reactive({
  id: 0,
  title: '',
  description: ''
})
const currentEditProject = ref(null)

// 统计
const stats = computed(() => {
  return {
    total: projects.value.length,
    completed: projects.value.filter(p => p.status === 'completed').length,
    inProgress: projects.value.filter(p => ['text_uploaded', 'settings_extracted', 'script_generated', 'voice_generated', 'images_generated', 'videos_generated'].includes(p.status)).length,
    draft: projects.value.filter(p => p.status === 'draft').length
  }
})

// 过滤后的项目列表
const filteredProjects = computed(() => {
  let result = projects.value

  // 状态筛选
  if (statusFilter.value) {
    if (statusFilter.value === 'in_progress') {
      result = result.filter(p => ['text_uploaded', 'settings_extracted', 'script_generated', 'voice_generated', 'images_generated', 'videos_generated'].includes(p.status))
    } else {
      result = result.filter(p => p.status === statusFilter.value)
    }
  }

  // 搜索筛选
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(p => 
      p.project_name.toLowerCase().includes(query) ||
      (p.description && p.description.toLowerCase().includes(query))
    )
  }

  return result
})

// 获取项目列表
const fetchProjects = async () => {
  loading.value = true
  try {
    const response = await getProjects({
      page: currentPage.value,
      per_page: pageSize.value
    })
    projects.value = response.data || []
    total.value = response.total || projects.value.length
  } catch (error) {
    console.error('Failed to fetch projects:', error)
    ElMessage.error('获取项目列表失败')
  } finally {
    loading.value = false
  }
}

// 状态相关方法
const getStatusType = (status) => {
  const typeMap = {
    'draft': 'info',
    'text_uploaded': 'warning',
    'settings_extracted': 'warning',
    'script_generated': 'warning',
    'voice_generated': 'warning',
    'images_generated': 'warning',
    'videos_generated': 'warning',
    'completed': 'success'
  }
  return typeMap[status] || 'info'
}

const getStatusLabel = (status) => {
  const labelMap = {
    'draft': '草稿',
    'text_uploaded': '已上传',
    'settings_extracted': '已提取设定',
    'setting_images_generated': '已生成设定图',
    'script_generated': '已生成剧本',
    'voice_generated': '已生成语音',
    'storyboard_generated': '已生成分镜',
    'videos_generated': '已生成视频',
    'completed': '已完成',
    // 中间状态
    'setting_image_generation': '设定图生成中'
  }
  return labelMap[status] || status
}

const getProgressPercent = (status) => {
  const progressMap = {
    'draft': 0,
    'text_uploaded': 15,
    'settings_extracted': 30,
    'script_generated': 45,
    'voice_generated': 60,
    'images_generated': 75,
    'videos_generated': 90,
    'completed': 100
  }
  return progressMap[status] || 0
}

const getProgressStatus = (status) => {
  if (status === 'completed') return 'success'
  if (status === 'draft') return ''
  return ''
}

const getProgressLabel = (status) => {
  const stepMap = {
    'draft': '待开始',
    'text_uploaded': '1/7',
    'settings_extracted': '2/7',
    'script_generated': '3/7',
    'voice_generated': '4/7',
    'images_generated': '5/7',
    'videos_generated': '6/7',
    'completed': '7/7'
  }
  return stepMap[status] || ''
}

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 创建项目
const handleCreate = async () => {
  const valid = await createFormRef.value.validate().catch(() => false)
  if (!valid) return

  creating.value = true
  try {
    const response = await createProject({
      title: createForm.title,
      description: createForm.description
    })
    ElMessage.success('项目创建成功')
    showCreateDialog.value = false
    createForm.title = ''
    createForm.description = ''
    await fetchProjects()
    
    // 询问是否立即进入项目
    try {
      await ElMessageBox.confirm('是否立即进入项目进行编辑？', '提示', {
        confirmButtonText: '立即进入',
        cancelButtonText: '稍后',
        type: 'info'
      })
      enterProject(response)
    } catch {
      // 用户选择稍后
    }
  } catch (error) {
    console.error('Failed to create project:', error)
    ElMessage.error('创建项目失败')
  } finally {
    creating.value = false
  }
}

// 进入项目
const enterProject = (project) => {
  router.push({
    name: 'workspace',
    params: { projectId: project.id }
  })
}

// 编辑项目
const editProject = (project) => {
  currentEditProject.value = project
  editForm.id = project.id
  editForm.title = project.project_name
  editForm.description = project.description || ''
  showEditDialog.value = true
}

const handleUpdate = async () => {
  const valid = await editFormRef.value.validate().catch(() => false)
  if (!valid) return

  updating.value = true
  try {
    await updateProject(editForm.id, {
      project_name: editForm.title,
      description: editForm.description
    })
    ElMessage.success('项目更新成功')
    showEditDialog.value = false
    await fetchProjects()
  } catch (error) {
    console.error('Failed to update project:', error)
    ElMessage.error('更新项目失败')
  } finally {
    updating.value = false
  }
}

// 删除项目
const confirmDelete = (project) => {
  ElMessageBox.confirm(
    `确定要删除项目 "${project.project_name}" 吗？此操作不可恢复。`,
    '确认删除',
    {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning'
    }
  ).then(async () => {
    try {
      await deleteProject(project.id)
      ElMessage.success('项目删除成功')
      await fetchProjects()
    } catch (error) {
      console.error('Failed to delete project:', error)
      ElMessage.error('删除项目失败')
    }
  }).catch(() => {
    // 取消删除
  })
}

// 搜索
const handleSearch = () => {
  currentPage.value = 1
}

// 分页
const handleSizeChange = (size) => {
  pageSize.value = size
  fetchProjects()
}

const handleCurrentChange = (page) => {
  currentPage.value = page
  fetchProjects()
}

onMounted(() => {
  fetchProjects()
})
</script>

<style scoped lang="scss">
.dashboard-view {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e4e7ed;

  .header-content {
    .page-title {
      font-size: 24px;
      font-weight: 600;
      color: #303133;
      margin: 0 0 8px;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .page-subtitle {
      font-size: 14px;
      color: #909399;
      margin: 0;
    }
  }
}

.stats-row {
  margin-bottom: 24px;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 20px;

  .stat-icon {
    width: 56px;
    height: 56px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 28px;
    margin-right: 16px;
  }

  .stat-info {
    .stat-value {
      font-size: 28px;
      font-weight: 600;
      color: #303133;
      line-height: 1;
      margin-bottom: 4px;
    }

    .stat-label {
      font-size: 14px;
      color: #909399;
    }
  }
}

.projects-card {
  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .card-title {
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }

    .header-actions {
      display: flex;
      gap: 12px;
    }
  }
}

.project-name-cell {
  display: flex;
  align-items: center;
  gap: 12px;

  .project-icon {
    font-size: 20px;
    color: #409eff;
  }

  .project-info {
    .project-name {
      font-weight: 500;
      color: #303133;
      margin-bottom: 4px;
    }

    .project-desc {
      font-size: 12px;
      color: #909399;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 200px;
    }
  }
}

.progress-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;

  .progress-text {
    font-size: 12px;
    color: #909399;
  }
}

.time-text {
  color: #606266;
  font-size: 13px;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}
</style>